/**
 * Whether the page's connection is slow for this player (P1.9-T46): judged against the player's own normal round trip,
 * not a fixed number, so a player whose line always sits at 350 ms never sees the mark, and one whose line is usually
 * 40 ms sees it when it climbs. The samples are the socket's keepalive round trips (`PerfLink`, one per
 * `HEARTBEAT_INTERVAL_MS`). Pure: a state in, a state out, plain data.
 */

/**
 * The rule's numbers, named so each is one edit.
 */
export const LINK_QUALITY = {
  /** The samples the baseline is the median of. */
  window: 60,
  /** The samples taken before the mark can show; the baseline is the first samples' median until then. */
  warmup: 10,
  /** A sample is over when it exceeds this many times the baseline... */
  slowFactor: 1.5,
  /** ...and the baseline plus this many milliseconds. */
  slowMarginMs: 150,
  /** Samples in a row that must be over to show Slow. */
  slowRun: 3,
  /** A sample is back when it is under the baseline plus this many milliseconds. */
  backMarginMs: 75,
  /** Samples in a row that must be back to clear Slow. */
  backRun: 2,
} as const;

/**
 * The state the rule carries between samples.
 */
export interface LinkQuality {
  /** The last `window` round trips in milliseconds, oldest first. */
  readonly samples: readonly number[];
  /** The count of samples taken since the state began, never reset by the window. */
  readonly taken: number;
  /** Samples in a row over the slow line. */
  readonly over: number;
  /** Samples in a row under the back line. */
  readonly back: number;
  /** Whether the connection reads as slow. */
  readonly slow: boolean;
}

/**
 * The state before any sample.
 */
export const LINK_QUALITY_START: LinkQuality = { samples: [], taken: 0, over: 0, back: 0, slow: false };

/**
 * The median of a list.
 *
 * @param values The values, at least one.
 * @returns The middle value, or the mean of the two middle ones.
 */
export function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 1 ? (sorted[mid] as number) : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

/**
 * Takes one round trip. The baseline is the median of the samples before this one, so a slow sample is judged against
 * the player's normal and not against itself. A sample that is over adds to the run of overs and breaks the run of
 * backs; one that is back does the reverse; one between the two lines breaks both. The mark shows after `slowRun`
 * overs once `warmup` samples have been taken, and clears after `backRun` backs.
 *
 * @param state The state before the sample.
 * @param rttMs The round trip in milliseconds.
 * @returns The state after it.
 * @example
 * let state = LINK_QUALITY_START;
 * for (const rtt of [40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 300, 300, 300]) state = takeSample(state, rtt);
 * state.slow;
 * // true
 */
export function takeSample(state: LinkQuality, rttMs: number): LinkQuality {
  const baseline = state.samples.length === 0 ? rttMs : median(state.samples);
  const over = rttMs > Math.max(LINK_QUALITY.slowFactor * baseline, baseline + LINK_QUALITY.slowMarginMs);
  const back = rttMs < baseline + LINK_QUALITY.backMarginMs;
  const overRun = over ? state.over + 1 : 0;
  const backRun = back ? state.back + 1 : 0;
  const taken = state.taken + 1;
  const samples = state.samples.length >= LINK_QUALITY.window ? [...state.samples.slice(1), rttMs] : [...state.samples, rttMs];
  let slow = state.slow;
  if (!slow && taken > LINK_QUALITY.warmup && overRun >= LINK_QUALITY.slowRun) {
    slow = true;
  } else if (slow && backRun >= LINK_QUALITY.backRun) {
    slow = false;
  }
  return { samples, taken, over: overRun, back: backRun, slow };
}
